import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { JOURNEYS, TRAVELLER_META, FILTERS } from "../data/journeys";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";

export default function JourneyDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const j = JOURNEYS.find((x) => x.slug === slug);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (j) document.title = `${j.title} — enei.in`;
    }, [j]);

    if (!j) {
        return (
            <div className="App bg-paper grain">
                <Navbar />
                <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
                    <p className="font-serif italic text-forest text-3xl mb-4">This journey has wandered off.</p>
                    <Link to="/journeys" className="text-terracotta uppercase tracking-[0.22em] text-[12px] border-b border-terracotta pb-1">
                        Back to all journeys
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const tMeta = TRAVELLER_META[j.traveller];
    const stateLabel = FILTERS.states.find((s) => s.id === j.state)?.label;

    const speakToCurator = () => {
        // Prefill the Plan-My-Trip form via sessionStorage
        sessionStorage.setItem(
            "enei_prefill",
            JSON.stringify({
                states: [j.state],
                traveller_type: j.traveller,
                message: `I'd like to know more about "${j.title}".`,
            })
        );
        navigate("/#plan");
    };

    return (
        <div className="App bg-paper grain">
            <Navbar />

            {/* Cinematic hero */}
            <section
                data-testid="journey-hero"
                className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-forest"
            >
                <img
                    src={j.image}
                    alt={j.title}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover animate-slow-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/95 via-forest/45 to-forest/35" />
                <div className="absolute inset-0 bg-gradient-to-b from-forest/40 via-transparent to-transparent" />

                <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
                    <Link
                        to={`/journeys?traveller=${j.traveller}`}
                        data-testid="back-to-list"
                        className="text-[11px] uppercase tracking-[0.28em] text-paper/80 hero-shadow mb-8 hover:text-marigold transition-colors w-fit"
                    >
                        ← {tMeta.title}
                    </Link>
                    <p className="text-marigold text-xs md:text-sm uppercase tracking-[0.32em] hero-shadow mb-6">
                        {stateLabel} · {j.season} · {j.duration}
                    </p>
                    <h1
                        data-testid="journey-title"
                        className="font-serif text-paper hero-shadow text-4xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.98] tracking-tight font-light max-w-5xl"
                    >
                        {j.title}
                    </h1>
                    <p className="mt-8 max-w-2xl font-serif italic text-paper/85 text-xl md:text-2xl leading-snug hero-shadow">
                        {j.oneLiner}
                    </p>
                </div>
            </section>

            {/* Intro essay */}
            <section className="bg-paper py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <aside className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-8">— What you'll feel</p>
                        <p className="font-serif italic text-forest text-2xl md:text-3xl leading-snug">
                            {j.mood}
                        </p>

                        <div className="mt-12 space-y-4 text-sm">
                            <Row label="State" value={stateLabel} />
                            <Row label="Best season" value={j.season} />
                            <Row label="Duration" value={j.duration} />
                            <Row label="Budget" value={j.budgetLabel} />
                            <Row label="For" value={tMeta.title} />
                        </div>
                    </aside>

                    <div className="col-span-12 md:col-span-8">
                        <p className="dropcap text-forest text-lg md:text-[19px] leading-relaxed">
                            {j.intro}
                        </p>
                    </div>
                </div>
            </section>

            {/* Day-by-day */}
            <section className="bg-stone py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14">
                    <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-8">— The journey, day by day</p>
                    <h2 className="font-serif text-forest text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight max-w-3xl mb-16">
                        Slow enough to <em className="italic">listen</em>. Long enough to be changed.
                    </h2>

                    <ol className="space-y-10 md:space-y-12">
                        {j.days.map((d, i) => (
                            <li
                                key={i}
                                data-testid={`day-${i}`}
                                className="grid grid-cols-12 gap-4 md:gap-8 pb-10 md:pb-12 border-b border-forest/15 last:border-0"
                            >
                                <div className="col-span-12 md:col-span-2">
                                    <p className="font-serif italic text-terracotta text-xl md:text-2xl">{d.d}</p>
                                </div>
                                <div className="col-span-12 md:col-span-4">
                                    <h3 className="font-serif text-forest text-2xl md:text-3xl font-light leading-tight">{d.t}</h3>
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <p className="text-slatewarm text-[15px] md:text-base leading-relaxed">{d.b}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* What's included */}
            <section className="bg-paper py-24 md:py-28">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-4">
                        <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">— What's quietly handled</p>
                    </div>
                    <ul className="col-span-12 md:col-span-8 space-y-5">
                        {j.included.map((it, i) => (
                            <li key={i} className="flex gap-5 items-baseline border-b border-forest/10 pb-5">
                                <span className="font-serif italic text-marigold text-base">{String(i + 1).padStart(2, "0")}</span>
                                <span className="text-forest text-[17px] md:text-lg leading-relaxed">{it}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Curator CTA */}
            <section className="bg-forest text-paper py-24 md:py-32">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-10">
                    <div className="col-span-12 md:col-span-7">
                        <p className="text-xs uppercase tracking-[0.28em] text-marigold mb-6">— Speak with the curator</p>
                        <h2 className="font-serif text-paper text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light tracking-tight">
                            One real human, one real conversation. <em className="italic text-marigold">No bots.</em>
                        </h2>
                        <p className="mt-8 text-paper/75 text-base md:text-[17px] max-w-xl leading-relaxed">
                            We'll write back within two days, usually with a few honest questions
                            before we shape the route. Tell us your dates, your pace, your appetite.
                            Tell us what you're afraid of. Tell us what you're hoping for. The rest
                            is craft.
                        </p>
                        <button
                            onClick={speakToCurator}
                            data-testid="speak-curator"
                            className="mt-10 inline-flex items-center gap-3 bg-paper text-forest px-8 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-marigold transition-all duration-300 hover:-translate-y-0.5"
                        >
                            Speak about this journey <span aria-hidden>→</span>
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 border-b border-forest/15 pb-2">
        <span className="text-[11px] uppercase tracking-[0.22em] text-slatewarm">{label}</span>
        <span className="text-forest text-sm md:text-base text-right">{value}</span>
    </div>
);
